// EventEdit.js
// TODO: break out into seperate files/components once we are fully seperated from the local-storage mock-data
import * as React from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Card,
  CardContent,
  IconButton,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack,
  ExpandMore,
  ExpandLess,
  Add as AddIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useNavigate, useBlocker, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  createTestEventWithScenariosAsync,
  selectCreateTestEventProgress,
  createAttributesOnExistingScenariosAsync,
} from '../../redux/flows/createEventSlice';
import {
  updateTestEventAsync,
  selectUpdateTestEventProgress,
  setProgress,
} from '../../redux/flows/updateEventSlice';
import { fetchFlowWithDetailsAsync } from '../../redux/flows/getFlowsSlice';
import { deleteAttributeAsync } from '../../redux/flows/deleteAttributeSlice';
import { deleteScenarioAsync } from '../../redux/flows/deleteScenarioSlice';
import SaveProgressDialog from '../shared/SaveProgressDialog';

// Service imports
import { generateTempScenarioId, generateTempAttributeId } from '../../utilities/tempIdGenerator';
import {
  categorizeEventChanges,
  summarizeChanges,
  hasChanges,
} from '../../utilities/flow/categorizeChanges';
import { SCENARIO_TYPES } from '../../utilities/consts';
import { eventCardStyles } from './styles/eventCardStyles';

export default function EventEdit({ flowId, eventId }) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  // Removed unused selector - flow data is loaded from mockFlowService

  const [currentEvent, setCurrentEvent] = React.useState({
    event_id: null,
    event_name: '',
    event_order: 1,
    scenarios: [],
    isNew: false,
  });

  const [originalEvent, setOriginalEvent] = React.useState(null);
  const [expandedScenarios, setExpandedScenarios] = React.useState({});
  const [addScenarioModalOpen, setAddScenarioModalOpen] = React.useState(false);
  const [newScenarioType, setNewScenarioType] = React.useState({
    scenario_name: '',
    scenario_type: 'inbound',
    cloneFromInboundId: '',
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
  const [validationError, setValidationError] = React.useState(null);
  const [errorLocations, setErrorLocations] = React.useState({
    attribute: {},
    scenario: {},
  });

  const [showUnsavedDialog, setShowUnsavedDialog] = React.useState(false);
  const [pendingNavigation, setPendingNavigation] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [showSpinner, setShowSpinner] = React.useState(false);
  const [contentReady, setContentReady] = React.useState(false);
  const [contentVisible, setContentVisible] = React.useState(false);
  const [isDiscarding, setIsDiscarding] = React.useState(false);
  const [showProgressDialog, setShowProgressDialog] = React.useState(false);
  const createProgress = useSelector(selectCreateTestEventProgress);
  const updateProgress = useSelector(selectUpdateTestEventProgress);
  // Use the appropriate progress based on operation type
  const saveProgress = currentEvent?.event_db_id ? updateProgress : createProgress;

  // Block navigation when there are unsaved changes
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname,
  );

  // Handle the blocked navigation
  React.useEffect(() => {
    if (blocker.state === 'blocked') {
      // Show the unsaved dialog
      setShowUnsavedDialog(true);
      setPendingNavigation(() => () => {
        blocker.proceed();
      });
    }
  }, [blocker]);

  // Handle market changes - show unsaved dialog if there are unsaved changes
  React.useEffect(() => {
    const handleMarketChange = () => {
      if (hasUnsavedChanges) {
        setShowUnsavedDialog(true);
        setPendingNavigation(() => () => {
          navigate('/flows');
        });
      } else {
        // No unsaved changes, navigate immediately
        navigate('/flows');
      }
    };

    window.addEventListener('marketChanged', handleMarketChange);

    return () => {
      window.removeEventListener('marketChanged', handleMarketChange);
    };
  }, [hasUnsavedChanges, navigate]);

  // Control spinner visibility with delay to prevent flicker on fast loads
  React.useEffect(() => {
    let timer;
    if (isLoading) {
      // Only show spinner after 300ms delay
      timer = setTimeout(() => setShowSpinner(true), 300);
    } else {
      setShowSpinner(false);
    }
    return () => clearTimeout(timer);
  }, [isLoading]);

  // Load event data from database
  React.useEffect(() => {
    const loadEventData = async () => {
      setIsLoading(true);
      setContentReady(false);
      setContentVisible(false);

      if (!flowId || !eventId) {
        setIsLoading(false);
        return;
      }

      // Check if this is a new event passed via navigation state
      if (location.state?.newEvent && location.state.newEvent.event_id.toString() === eventId) {
        const newEvent = location.state.newEvent;
        setCurrentEvent(newEvent);
        setOriginalEvent(null);
        setHasUnsavedChanges(true);
        setIsLoading(false);

        // Trigger fade-in animation
        setTimeout(() => {
          setContentReady(true);
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              setContentVisible(true);
            });
          });
        }, 100);
        return;
      }

      // Always load from database
      try {
        const result = await dispatch(fetchFlowWithDetailsAsync(flowId)).unwrap();
        const flowData = JSON.parse(JSON.stringify(result));

        if (flowData && flowData.events) {
          const event = flowData.events.find((e) => {
            const eventIdStr = e.event_id ? e.event_id.toString() : '';
            const eventDbIdStr = e.event_db_id ? e.event_db_id.toString() : '';
            return eventDbIdStr === eventId || eventIdStr === eventId;
          });

          if (event) {
            const eventCopy = JSON.parse(JSON.stringify(event));
            setCurrentEvent(eventCopy);
            const savedEvent = event.event_db_id ? JSON.parse(JSON.stringify(event)) : null;
            setOriginalEvent(savedEvent);
            setHasUnsavedChanges(event.isNew || !savedEvent);
          } else {
            navigate(`/flows/edit/${flowId}`);
          }
        } else {
          console.error('Flow not found with ID:', flowId);
          alert(`Flow with ID "${flowId}" not found`);
          navigate('/flows');
        }
      } catch (error) {
        console.error('Error fetching flow from database:', error);
        alert(`Failed to load flow: ${error.message}`);
        navigate('/flows');
      }

      setIsLoading(false);
      // Two-step fade-in: first render at opacity 0, then fade to opacity 1
      setTimeout(() => {
        setContentReady(true);
        // Force browser to render at opacity 0 first with double RAF
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setContentVisible(true);
          });
        });
      }, 100);
    };

    loadEventData();
  }, [flowId, eventId, dispatch, navigate, location.state]);

  // Track changes to detect unsaved modifications
  React.useEffect(() => {
    if (!currentEvent.event_id || isDiscarding) return;

    let nextHasChanges = false;

    if (currentEvent.isNew) {
      nextHasChanges = true;
    } else if (originalEvent) {
      // Use our utility to properly detect changes
      const changes = categorizeEventChanges(currentEvent, originalEvent);
      nextHasChanges = hasChanges(changes);

      // Log changes for debugging
      if (nextHasChanges) {
        // Uncomment for debugging:
        // console.log("Detected changes:", { current: currentEvent, original: originalEvent, changes });
      }
    }

    setHasUnsavedChanges(nextHasChanges);
    // Database flows keep changes in React state only
  }, [currentEvent, originalEvent, flowId, isDiscarding]);

  // Add browser refresh/close warning for unsaved changes
  React.useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        // Modern browsers require this specific approach
        e.preventDefault();
        e.returnValue = ''; // Chrome requires returnValue to be set
        // Most browsers will show their own generic message
        // Custom messages are largely ignored for security reasons
        return 'You have unsaved changes. Are you sure you want to leave?';
      }
    };

    if (hasUnsavedChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // Navigation with unsaved changes check
  const handleNavigation = (navigationFn) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(() => navigationFn);
      setShowUnsavedDialog(true);
    } else {
      navigationFn();
    }
  };

  const handleConfirmNavigation = () => {
    // Set flag to prevent any final state updates
    setIsDiscarding(true);

    // Changes are only in React state and will be lost on navigation

    // Close dialog immediately
    setShowUnsavedDialog(false);
    setHasUnsavedChanges(false);

    // Navigate after a brief delay to ensure cleanup
    setTimeout(() => {
      if (pendingNavigation) {
        pendingNavigation();
        setPendingNavigation(null);
      }
      setIsDiscarding(false);
    }, 50);
  };

  const handleCancelNavigation = () => {
    setShowUnsavedDialog(false);
    setPendingNavigation(null);
    // Reset the blocker if it was triggered
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  };

  const handleSaveEvent = async (andNavigate = false) => {
    try {
      if (!currentEvent.event_id) {
        alert('Error: Event ID is missing. Cannot save.');
        return;
      }

      if (!currentEvent.event_name.trim()) {
        alert('Please enter an event name');
        return;
      }

      // Validate that all attributes have both name and expected value
      // Skip schema-derived rows — their name/expected are locked and cannot be edited
      const hasEmptyAttributes = currentEvent.scenarios.some((scenario) =>
        scenario.scenario_attributes?.some(
          (attr) =>
            !attr.is_confluence_validation_schema_value &&
            (!attr.attribute_name?.trim() || !attr.expected_value?.trim()),
        ),
      );

      if (hasEmptyAttributes) {
        alert(
          'Cannot save: All attributes must have both a name and an expected value. Please complete all attribute fields or remove empty attributes.',
        );
        return;
      }

      // Check if any scenarios are missing matching attributes
      const scenariosWithoutMatching = currentEvent.scenarios.filter(
        (scenario) =>
          !scenario.matching_attribute_name || !scenario.matching_attribute_value?.trim(),
      );

      if (scenariosWithoutMatching.length > 0) {
        const confirmed = window.confirm(
          `Warning: ${scenariosWithoutMatching.length} scenario(s) do not have match key and match value set.\n\n` +
            `Scenarios without match key/value:\n${scenariosWithoutMatching.map((s) => `- ${s.scenario_name}`).join('\n')}\n\n` +
            `Do you want to continue saving without match key/value?`,
        );
        if (!confirmed) {
          return;
        }
      }

      // Update event name
      const eventToSave = {
        ...currentEvent,
        event_name: currentEvent.event_name.trim(),
        isNew: false,
      };

      // Categorize what needs to be created, updated, or deleted
      const changes = categorizeEventChanges(eventToSave, originalEvent);
      const changeSummary = summarizeChanges(changes);

      // Determine if this is a CREATE or UPDATE operation
      const isNewEvent = !currentEvent.event_db_id;

      // Get flow database ID from URL (all flows are now database flows)
      const flowDbId = flowId;

      if (!flowDbId) {
        console.error('No flow database ID available.');
        alert('This flow needs to be saved to the database first before adding events.');
        return;
      }

      // Prepare data for API
      const eventData = {
        name: eventToSave.event_name,
        description: '', // Add description field if needed
        order: eventToSave.event_order,
      };

      // Prepare scenarios for API
      const scenarios = eventToSave.scenarios.map((scenario) => ({
        name: scenario.scenario_name,
        type: scenario.scenario_type,
        description: '',
        matching_attribute_name: scenario.matching_attribute_name || null,
        matching_attribute_value: scenario.matching_attribute_value || null,
      }));

      // Prepare attributes with scenario index mapping
      const attributes = [];
      eventToSave.scenarios.forEach((scenario, scenarioIndex) => {
        scenario.scenario_attributes?.forEach((attr) => {
          // Schema-linked attributes must not be sent to insert-test-flow
          if (attr.is_confluence_validation_schema_value) return;
          attributes.push({
            scenarioIndex: scenarioIndex,
            name: attr.attribute_name,
            expectedValue: attr.expected_value, // This goes to 'expected' field in the API
            type: attr.attribute_type || 'Static', // This goes to 'config_type' field in the API
          });
        });
      });

      // Show progress dialog and call API
      setShowProgressDialog(true);

      let result;

      if (isNewEvent) {
        // CREATE new event - use existing logic

        result = await dispatch(
          createTestEventWithScenariosAsync(flowDbId, eventData, scenarios, attributes),
        );

        if (result.success && result.eventId) {
          // Update the event with the API-assigned ID
          eventToSave.event_db_id = result.eventId;

          // Update scenario IDs from API response
          if (result.scenarioIds && result.scenarioIds.length > 0) {
            // Map the returned scenario IDs to the scenarios in order
            const inboundScenarioIds = result.scenarioIds
              .filter((s) => s.type === 'inbound')
              .map((s) => s.id);
            const outboundScenarioIds = result.scenarioIds
              .filter((s) => s.type !== 'inbound')
              .map((s) => s.id);

            let inboundIndex = 0;
            let outboundIndex = 0;

            eventToSave.scenarios = eventToSave.scenarios.map((scenario) => {
              if (
                scenario.scenario_type === 'inbound' &&
                inboundIndex < inboundScenarioIds.length
              ) {
                return { ...scenario, scenario_db_id: inboundScenarioIds[inboundIndex++] };
              } else if (
                scenario.scenario_type !== 'inbound' &&
                outboundIndex < outboundScenarioIds.length
              ) {
                return { ...scenario, scenario_db_id: outboundScenarioIds[outboundIndex++] };
              }
              return scenario;
            });
          }

          // Update attribute IDs from API response
          if (result.attributeIds) {
            // Distribute attribute IDs to the correct attributes based on order
            let inboundAttrIndex = 0;
            let outboundAttrIndex = 0;

            eventToSave.scenarios = eventToSave.scenarios.map((scenario) => {
              if (scenario.scenario_attributes && scenario.scenario_attributes.length > 0) {
                const updatedAttributes = scenario.scenario_attributes.map((attr) => {
                  if (
                    scenario.scenario_type === 'inbound' &&
                    result.attributeIds.inbound &&
                    inboundAttrIndex < result.attributeIds.inbound.length
                  ) {
                    return {
                      ...attr,
                      attribute_db_id: result.attributeIds.inbound[inboundAttrIndex++],
                    };
                  } else if (
                    scenario.scenario_type !== 'inbound' &&
                    result.attributeIds.outbound &&
                    outboundAttrIndex < result.attributeIds.outbound.length
                  ) {
                    return {
                      ...attr,
                      attribute_db_id: result.attributeIds.outbound[outboundAttrIndex++],
                    };
                  }
                  return attr;
                });
                return { ...scenario, scenario_attributes: updatedAttributes };
              }
              return scenario;
            });
          }
        }
      } else {
        // UPDATE existing event

        // Handle UPDATE operations
        const updateResult = await dispatch(updateTestEventAsync(changes));

        // Handle CREATE operations for new items on existing event
        if (changes.scenarios.create.length > 0 || changes.attributes.create.length > 0) {
          // Handle new scenarios with their attributes
          if (changes.scenarios.create.length > 0) {
            const newScenarios = changes.scenarios.create.map((scenario) => ({
              name: scenario.scenario_name,
              type: scenario.scenario_type,
              description: scenario.description || '',
              matching_attribute_name: scenario.matching_attribute_name || null,
              matching_attribute_value: scenario.matching_attribute_value || null,
            }));

            // Collect attributes for new scenarios only
            const newScenarioAttributes = [];
            changes.scenarios.create.forEach((scenario, scenarioIndex) => {
              (scenario.scenario_attributes || []).forEach((attr) => {
                // Schema-linked attributes must not be sent to insert-test-flow
                if (attr.is_confluence_validation_schema_value) return;
                newScenarioAttributes.push({
                  scenarioIndex,
                  name: attr.attribute_name,
                  expectedValue: attr.expected_value,
                  type: attr.attribute_type || 'Static',
                });
              });
            });

            // Call create for new scenarios with their attributes
            const createResult = await dispatch(
              createTestEventWithScenariosAsync(
                flowDbId,
                eventData,
                newScenarios,
                newScenarioAttributes,
                eventToSave.event_db_id, // Pass the existing event ID to skip event creation
              ),
            );

            if (!createResult.success) {
              console.error('Failed to create new items:', createResult.error);
            } else if (createResult.scenarioIds || createResult.attributeIds) {
              // Update the new items with their database IDs
              // Find the scenarios that were just created and update their db_ids
              let newScenarioIndex = 0;
              eventToSave.scenarios = eventToSave.scenarios.map((scenario) => {
                if (
                  !scenario.scenario_db_id &&
                  createResult.scenarioIds &&
                  newScenarioIndex < createResult.scenarioIds.length
                ) {
                  const scenarioIdInfo = createResult.scenarioIds[newScenarioIndex++];
                  return { ...scenario, scenario_db_id: scenarioIdInfo.id };
                }
                return scenario;
              });

              // Update attribute IDs for new attributes
              if (createResult.attributeIds) {
                let inboundAttrIndex = 0;
                let outboundAttrIndex = 0;

                eventToSave.scenarios = eventToSave.scenarios.map((scenario) => {
                  if (scenario.scenario_attributes && scenario.scenario_attributes.length > 0) {
                    const updatedAttributes = scenario.scenario_attributes.map((attr) => {
                      if (!attr.attribute_db_id) {
                        if (
                          scenario.scenario_type === 'inbound' &&
                          createResult.attributeIds.inbound &&
                          inboundAttrIndex < createResult.attributeIds.inbound.length
                        ) {
                          return {
                            ...attr,
                            attribute_db_id: createResult.attributeIds.inbound[inboundAttrIndex++],
                          };
                        } else if (
                          scenario.scenario_type !== 'inbound' &&
                          createResult.attributeIds.outbound &&
                          outboundAttrIndex < createResult.attributeIds.outbound.length
                        ) {
                          return {
                            ...attr,
                            attribute_db_id:
                              createResult.attributeIds.outbound[outboundAttrIndex++],
                          };
                        }
                      }
                      return attr;
                    });
                    return { ...scenario, scenario_attributes: updatedAttributes };
                  }
                  return scenario;
                });
              }
            }
          }

          // Handle new attributes on existing scenarios separately
          const newAttributesOnExistingScenarios = changes.attributes.create.filter(
            (attr) => attr.scenario_db_id,
          );
          if (newAttributesOnExistingScenarios.length > 0) {
            const attrResult = await dispatch(
              createAttributesOnExistingScenariosAsync(newAttributesOnExistingScenarios),
            );

            if (attrResult.success && attrResult.attributeIds) {
              // Update attribute IDs for newly created attributes
              let inboundAttrIndex = 0;
              let outboundAttrIndex = 0;

              eventToSave.scenarios = eventToSave.scenarios.map((scenario) => {
                if (scenario.scenario_db_id && scenario.scenario_attributes) {
                  const updatedAttributes = scenario.scenario_attributes.map((attr) => {
                    if (!attr.attribute_db_id) {
                      if (
                        scenario.scenario_type === 'inbound' &&
                        attrResult.attributeIds.inboundIds &&
                        inboundAttrIndex < attrResult.attributeIds.inboundIds.length
                      ) {
                        return {
                          ...attr,
                          attribute_db_id: attrResult.attributeIds.inboundIds[inboundAttrIndex++],
                        };
                      } else if (
                        scenario.scenario_type !== 'inbound' &&
                        attrResult.attributeIds.outboundIds &&
                        outboundAttrIndex < attrResult.attributeIds.outboundIds.length
                      ) {
                        return {
                          ...attr,
                          attribute_db_id: attrResult.attributeIds.outboundIds[outboundAttrIndex++],
                        };
                      }
                    }
                    return attr;
                  });
                  return { ...scenario, scenario_attributes: updatedAttributes };
                }
                return scenario;
              });
            } else if (!attrResult.success) {
              console.error('Failed to create attributes on existing scenarios:', attrResult.error);
            }
          }
        }

        // Handle DELETE operations
        // IMPORTANT: Delete in reverse hierarchy order to avoid cascade conflicts:
        // 1. Attributes first (most granular)
        // 2. Scenarios second (cascade deletes remaining attributes)
        // 3. Events third (future - cascade deletes scenarios + attributes)
        // 4. Flows last (future - cascade deletes everything)
        if (changes.scenarios.delete.length > 0 || changes.attributes.delete.length > 0) {
          dispatch(setProgress('deleting'));

          const deletePromises = [];

          // Step 1: Delete attributes first (most granular level)
          if (changes.attributes.delete.length > 0) {
            changes.attributes.delete.forEach((attr) => {
              if (attr.attribute_db_id) {
                deletePromises.push(
                  dispatch(deleteAttributeAsync(attr.attribute_db_id, attr.scenario_type))
                    .then((result) => ({
                      type: 'DELETE_ATTRIBUTE',
                      attributeId: attr.attribute_db_id,
                      success: result.success,
                      error: result.error,
                    }))
                    .catch((error) => ({
                      type: 'DELETE_ATTRIBUTE',
                      attributeId: attr.attribute_db_id,
                      success: false,
                      error: error.message,
                    })),
                );
              }
            });
          }

          // Wait for all attribute deletions to complete
          if (deletePromises.length > 0) {
            const attributeDeleteResults = await Promise.all(deletePromises);
            const attributeFailures = attributeDeleteResults.filter((r) => !r.success);
            if (attributeFailures.length > 0) {
              console.error('Some attribute delete operations failed:', attributeFailures);
            }
          }

          // Step 2: Delete scenarios second (will cascade delete any remaining attributes)
          const scenarioDeletePromises = [];
          if (changes.scenarios.delete.length > 0) {
            changes.scenarios.delete.forEach((scenario) => {
              if (scenario.scenario_db_id) {
                scenarioDeletePromises.push(
                  dispatch(deleteScenarioAsync(scenario.scenario_db_id, scenario.scenario_type))
                    .then((result) => ({
                      type: 'DELETE_SCENARIO',
                      scenarioId: scenario.scenario_db_id,
                      success: result.success,
                      error: result.error,
                    }))
                    .catch((error) => ({
                      type: 'DELETE_SCENARIO',
                      scenarioId: scenario.scenario_db_id,
                      success: false,
                      error: error.message,
                    })),
                );
              }
            });
          }

          if (scenarioDeletePromises.length > 0) {
            const scenarioDeleteResults = await Promise.all(scenarioDeletePromises);
            const scenarioFailures = scenarioDeleteResults.filter((r) => !r.success);
            if (scenarioFailures.length > 0) {
              console.error('Some scenario delete operations failed:', scenarioFailures);
            }
          }
        }

        result = updateResult;
      }

      // Hide progress dialog after a brief delay to show completion
      setTimeout(() => {
        setShowProgressDialog(false);
      }, 1000);

      if (!result || !result.success) {
        console.error('API save failed:', result?.error);
        alert(`Warning: Could not save to database (${result?.error}).`);
      }

      // Update local state - use deep copies to avoid reference issues
      const savedEventCopy = JSON.parse(JSON.stringify(eventToSave));
      setCurrentEvent(savedEventCopy);
      setOriginalEvent(JSON.parse(JSON.stringify(savedEventCopy)));
      setHasUnsavedChanges(false);

      // Re-fetch flow details so saved dynamic schema values reflect back in UI
      if (result && result.success) {
        dispatch(fetchFlowWithDetailsAsync(flowId));
      }

      // Trigger FlowList refresh
      window.dispatchEvent(new Event('flowUpdated'));

      // If this was a new event that just got a database ID, update the URL
      if (isNewEvent && eventToSave.event_db_id && eventToSave.event_db_id !== eventId) {
        // Replace the current URL with the database ID instead of temp ID
        const newUrl = `/flows/edit/${flowId}/event/${eventToSave.event_db_id}`;
        window.history.replaceState(null, '', newUrl);
        // Note: We use replaceState instead of navigate to avoid adding to history
      }

      // If this was triggered from the navigation dialog
      if (andNavigate) {
        setShowUnsavedDialog(false);
        // Execute the pending navigation after a brief delay to ensure state updates
        setTimeout(() => {
          if (pendingNavigation) {
            pendingNavigation();
            setPendingNavigation(null);
          }
        }, 100);
      } else {
        alert('Event saved successfully!');
      }
    } catch (error) {
      console.error('Error saving event:', error);
      alert(`Error saving event: ${error.message}`);
    }
  };
  const handleBackToFlow = () => {
    handleNavigation(() => navigate(`/flows/edit/${flowId}`));
  };

  const toggleScenarioExpansion = (scenarioId) => {
    setExpandedScenarios((prev) => ({
      ...prev,
      [scenarioId]: !prev[scenarioId],
    }));
  };

  const handleAddScenarioType = () => {
    if (!newScenarioType.scenario_name.trim()) {
      alert('Please enter a scenario name');
      return;
    }

    const nextScenarioId = generateTempScenarioId();
    const newScenario = {
      scenario_id: nextScenarioId,
      scenario_name: newScenarioType.scenario_name.trim(),
      scenario_type: newScenarioType.scenario_type,
      scenario_attributes: [],
      matching_attribute_name: '',
      matching_attribute_value: '',
    };

    if (
      newScenarioType.scenario_type?.startsWith('outbound') &&
      newScenarioType.cloneFromInboundId
    ) {
      const source = (currentEvent.scenarios || []).find(
        (s) => s.scenario_id === newScenarioType.cloneFromInboundId,
      );
      if (source) {
        const clonedAttrs = (source.scenario_attributes || []).map((attr) => ({
          attribute_id: generateTempAttributeId(),
          attribute_name: attr.attribute_name || '',
          expected_value: attr.expected_value || '',
          attribute_type: attr.attribute_type || 'Static',
        }));

        newScenario.scenario_attributes = clonedAttrs;
        newScenario.matching_attribute_name = source.matching_attribute_name || '';
        newScenario.matching_attribute_value = source.matching_attribute_value || '';
        if (!newScenarioType.scenario_name.trim()) {
          newScenario.scenario_name = `${source.scenario_name} - clone`;
        }
      }
    }

    // Add scenario to current event
    setCurrentEvent((prev) => ({
      ...prev,
      scenarios: [...(prev.scenarios || []), newScenario],
    }));

    // Auto-expand the new scenario
    setExpandedScenarios((prev) => ({
      ...prev,
      [newScenario.scenario_id]: true,
    }));

    // Reset modal
    setNewScenarioType({
      scenario_name: '',
      scenario_type: 'inbound',
      cloneFromInboundId: '',
    });
    setAddScenarioModalOpen(false);
    setHasUnsavedChanges(true);
  };

  const handleDeleteScenario = (scenarioId) => {
    if (window.confirm('Are you sure you want to delete this scenario?')) {
      setCurrentEvent((prev) => ({
        ...prev,
        scenarios: prev.scenarios.filter((scenario) => scenario.scenario_id !== scenarioId),
      }));

      // Remove from expanded state
      setExpandedScenarios((prev) => {
        const newState = { ...prev };
        delete newState[scenarioId];
        return newState;
      });
    }
  };

  const handleAddAttribute = (scenarioId) => {
    const nextAttributeId = generateTempAttributeId();
    const newAttribute = {
      attribute_id: nextAttributeId,
      attribute_name: '',
      expected_value: '',
      attribute_type: 'Static',
    };

    setCurrentEvent((prev) => ({
      ...prev,
      scenarios: prev.scenarios.map((scenario) =>
        scenario.scenario_id === scenarioId
          ? {
              ...scenario,
              scenario_attributes: [...(scenario.scenario_attributes || []), newAttribute],
            }
          : scenario,
      ),
    }));
  };
  const handleDeleteAttribute = (scenarioId, attributeId) => {
    if (window.confirm('Are you sure you want to delete this attribute?')) {
      setCurrentEvent((prev) => ({
        ...prev,
        scenarios: prev.scenarios.map((scenario) =>
          scenario.scenario_id === scenarioId
            ? {
                ...scenario,
                scenario_attributes: scenario.scenario_attributes.filter(
                  (attr) => attr.attribute_id !== attributeId,
                ),
              }
            : scenario,
        ),
      }));
    }
  };

  const handleAttributeChange = (scenarioId, attributeId, field, value) => {
    setCurrentEvent((prev) => {
      const updated = {
        ...prev,
        scenarios: prev.scenarios.map((scenario) =>
          scenario.scenario_id === scenarioId
            ? {
                ...scenario,
                scenario_attributes: scenario.scenario_attributes.map((attr) =>
                  attr.attribute_id === attributeId ? { ...attr, [field]: value } : attr,
                ),
              }
            : scenario,
        ),
      };
      validateWhitespace(updated);
      return updated;
    });
  };

  const handleScenarioNameChange = (scenarioId, newName) => {
    setCurrentEvent((prev) => {
      const updated = {
        ...prev,
        scenarios: prev.scenarios.map((scenario) =>
          scenario.scenario_id === scenarioId ? { ...scenario, scenario_name: newName } : scenario,
        ),
      };
      validateWhitespace(updated);
      return updated;
    });
  };

  const handleMatchingAttributeChange = (scenarioId, field, value) => {
    setCurrentEvent((prev) => {
      const updated = {
        ...prev,
        scenarios: prev.scenarios.map((scenario) =>
          scenario.scenario_id === scenarioId ? { ...scenario, [field]: value } : scenario,
        ),
      };
      validateWhitespace(updated);
      return updated;
    });
  };

  /**
   * Validates whitespace in event data to prevent data quality issues.
   *
   * Validation Rules:
   * - Scenario Names: Cannot start with whitespace (leading spaces cause API issues)
   * - Attribute Names: Cannot contain ANY whitespace (dot notation paths must be clean)
   * - Expected Values: Cannot start with whitespace (leading spaces cause matching failures)
   *
   * @param {Object} eventData - The event data to validate
   * @returns {boolean} - True if validation passes, false if errors exist
   */
  const validateWhitespace = React.useCallback((eventData) => {
    const scenarioErrors = {};
    const attributeErrors = {};
    const messages = new Set(); // Use Set to automatically deduplicate messages

    for (const scenario of eventData.scenarios || []) {
      // Scenario Name — cannot start with whitespace
      if (/^\s/.test(scenario.scenario_name || '')) {
        scenarioErrors[scenario.scenario_id] = true;
        messages.add('Whitespace detected at beginning of Scenario Name');
      }

      // Match Key/Value Validation — both fields must be filled or both must be empty
      const hasMatchingName = Boolean(scenario.matching_attribute_name);
      const hasMatchingValue = Boolean(scenario.matching_attribute_value?.trim());

      if (hasMatchingName !== hasMatchingValue) {
        scenarioErrors[scenario.scenario_id] = true;
        messages.add('Match key requires both key and value');
      }

      // Match Value — cannot start with whitespace if filled
      if (hasMatchingValue && /^\s/.test(scenario.matching_attribute_value || '')) {
        scenarioErrors[scenario.scenario_id] = true;
        messages.add('Whitespace detected at beginning of Match Value');
      }

      for (const attr of scenario.scenario_attributes || []) {
        const attrError = {};

        // Attribute Name — cannot contain any whitespace
        if (/\s/.test(attr.attribute_name || '')) {
          attrError.name = true;
          messages.add('Whitespace detected in Attribute Name');
        }

        // Expected Value — cannot start with whitespace
        if (/^\s/.test(attr.expected_value || '')) {
          attrError.expected = true;
          messages.add('Whitespace detected at beginning of Expected Value');
        }

        if (Object.keys(attrError).length > 0) {
          attributeErrors[attr.attribute_id] = attrError;
        }
      }
    }

    // Combine unique messages (Set automatically deduplicates)
    const uniqueMessages = Array.from(messages);
    setValidationError(uniqueMessages.length > 0 ? uniqueMessages.join(' | ') : null);
    setErrorLocations({ attribute: attributeErrors, scenario: scenarioErrors });

    // Return whether validation passed
    return uniqueMessages.length === 0;
  }, []);

  const getScenarioTypeConfig = (scenarioType) => {
    return SCENARIO_TYPES.find((type) => type.value === scenarioType) || SCENARIO_TYPES[0];
  };

  // Removed - multiple inbound scenarios are now allowed

  if (isLoading) {
    return (
      <Box
        sx={{
          p: 3,
          minHeight: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: showSpinner ? 1 : 0,
          transition: 'opacity 0.2s ease-in-out',
        }}
      >
        <CircularProgress
          size="4rem"
          sx={{
            color: 'success.light',
          }}
        />
      </Box>
    );
  }
  if (!contentReady) {
    return null; // Don't render at all until ready
  }

  // Compute once in render scope: true when any scenario in this event is linked to a
  // confluence schema. When schemaLocked is true, event-name, scenario fields, and
  // add/remove controls are locked.
  const isSchemaEvent = (scenario_list) =>
    (scenario_list || []).some((s) => s.confluence_validation_schema_id != null);
  const schemaLocked = isSchemaEvent(currentEvent?.scenarios);

  return (
    <Box
      sx={{
        p: 3,
        opacity: contentVisible ? 1 : 0,
        transition: 'opacity 0.4s ease-in-out',
        willChange: 'opacity',
      }}
    >
      {/* Header with unsaved changes indicator */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={handleBackToFlow} sx={{ mr: 1, color: 'primary.main' }}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h4" component="h1">
              Edit Event: {currentEvent.event_name}
            </Typography>
            {hasUnsavedChanges && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <WarningIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 500 }}>
                  {currentEvent.isNew ? 'New event' : 'Unsaved changes'}
                </Typography>
              </Box>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            Flow ID: {flowId} | Event ID:{' '}
            {currentEvent.event_db_id || `${currentEvent.event_id} (temporary)`}
          </Typography>
        </Box>
      </Box>

      {/* Unsaved changes alert */}
      {hasUnsavedChanges && (
        <Alert severity={currentEvent.isNew ? 'info' : 'warning'} sx={{ mb: 3 }}>
          {currentEvent.isNew
            ? "This is a new event. Click 'Save Event' to add it to the flow permanently."
            : 'You have unsaved changes. Remember to save your work before leaving this page.'}
        </Alert>
      )}

      {/* Validation error alert */}
      {validationError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {validationError}
        </Alert>
      )}

      {/* Event Details Form */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Event Details
        </Typography>
        <TextField
          label="Event Name"
          value={currentEvent.event_name}
          onChange={(e) => setCurrentEvent((prev) => ({ ...prev, event_name: e.target.value }))}
          fullWidth
          required
          disabled={schemaLocked}
          sx={{
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: 'text.secondary',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
              },
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: 'primary.main',
            },
          }}
        />
      </Paper>

      {/* Scenario Types Section */}
      <Paper sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6">Scenarios ({currentEvent.scenarios?.length || 0})</Typography>
            {/* {hasInboundScenario() && (
      <Chip
        label="Has Inbound"
        color="primary"
        size="small"
        sx={{ fontSize: "0.75rem" }}
      />
    )} */}
          </Box>
          {!schemaLocked && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddScenarioModalOpen(true)}
              sx={{
                bgcolor: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.light',
                },
              }}
            >
              Add Scenario
            </Button>
          )}
        </Box>

        {!currentEvent.scenarios || currentEvent.scenarios.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            <Typography>No scenarios yet. Add your first scenario to get started.</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {currentEvent.scenarios.map((scenario) => {
              const typeConfig = getScenarioTypeConfig(scenario.scenario_type);
              const isExpanded = expandedScenarios[scenario.scenario_id];

              return (
                <Card key={scenario.scenario_id}>
                  <CardContent sx={{ pb: 1 }}>
                    {/* Scenario Header */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        mb: 1,
                      }}
                      onClick={() => toggleScenarioExpansion(scenario.scenario_id)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <TextField
                          value={scenario.scenario_name}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleScenarioNameChange(scenario.scenario_id, e.target.value);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          size="small"
                          variant="standard"
                          disabled={schemaLocked}
                          sx={{
                            '& .MuiInput-root': {
                              fontSize: '1rem',
                              fontWeight: 500,
                            },
                            '& .MuiInput-underline:before': {
                              borderBottom: (theme) =>
                                errorLocations?.scenario?.[scenario.scenario_id]
                                  ? `1px solid ${theme.palette.error.main}`
                                  : `1px dashed ${theme.palette.divider}`,
                            },
                            '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                              borderBottom: (theme) =>
                                errorLocations?.scenario?.[scenario.scenario_id]
                                  ? `1px solid ${theme.palette.error.main}`
                                  : `1px solid ${theme.palette.text.primary}`,
                            },
                            '& .MuiInput-underline:after': {
                              borderBottomColor: errorLocations?.scenario?.[scenario.scenario_id]
                                ? 'error.main'
                                : 'primary.main',
                            },
                            minWidth: '150px',
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          ID: {scenario.scenario_db_id || `${scenario.scenario_id} (temp)`}
                        </Typography>
                        <Box
                          sx={{
                            backgroundColor: 'transparent',
                            color: typeConfig.color,
                            border: `1px solid ${typeConfig.borderColor}`,
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            display: 'inline-flex',
                            alignItems: 'center',
                          }}
                        >
                          {typeConfig.label}
                        </Box>

                        {/* Match Key and Match Value Fields */}
                        <FormControl
                          size="small"
                          sx={{ minWidth: 150 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Select
                            value={scenario.matching_attribute_name || ''}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleMatchingAttributeChange(
                                scenario.scenario_id,
                                'matching_attribute_name',
                                e.target.value,
                              );
                            }}
                            displayEmpty
                            disabled={schemaLocked}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '&:hover fieldset': { borderColor: 'text.secondary' },
                                '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                              },
                            }}
                          >
                            <MenuItem value="">
                              <em>Match Key</em>
                            </MenuItem>
                            <MenuItem value="beacon.id">beacon.id</MenuItem>
                            <MenuItem value="link_name">link_name</MenuItem>
                            <MenuItem value="legacy_beacon_id">legacy_beacon_id</MenuItem>
                          </Select>
                        </FormControl>

                        <TextField
                          value={scenario.matching_attribute_value || ''}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleMatchingAttributeChange(
                              scenario.scenario_id,
                              'matching_attribute_value',
                              e.target.value,
                            );
                          }}
                          onClick={(e) => e.stopPropagation()}
                          placeholder="Match value"
                          size="small"
                          disabled={schemaLocked}
                          sx={{ minWidth: 150 }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {!schemaLocked && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteScenario(scenario.scenario_id);
                            }}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                        {isExpanded ? <ExpandLess /> : <ExpandMore />}
                      </Box>
                    </Box>

                    {/* Collapsible Attributes Table */}
                    <Collapse in={isExpanded}>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Attributes ({scenario.scenario_attributes?.length || 0})
                        </Typography>

                        {!scenario.scenario_attributes ||
                        scenario.scenario_attributes.length === 0 ? (
                          <Box
                            sx={{
                              textAlign: 'center',
                              py: 2,
                              color: 'text.secondary',
                            }}
                          >
                            <Typography variant="body2">No attributes yet</Typography>
                          </Box>
                        ) : (
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Attribute Name</TableCell>
                                  <TableCell>Expected Value</TableCell>
                                  <TableCell>Type</TableCell>
                                  <TableCell>Criticality</TableCell>
                                  <TableCell width={100}>Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {scenario.scenario_attributes.map((attribute) => (
                                  <TableRow key={attribute.attribute_id}>
                                    <TableCell>
                                      <TextField
                                        size="small"
                                        value={attribute.attribute_name}
                                        onChange={(e) =>
                                          handleAttributeChange(
                                            scenario.scenario_id,
                                            attribute.attribute_id,
                                            'attribute_name',
                                            e.target.value,
                                          )
                                        }
                                        placeholder="e.g. data.custom_attributes.screen"
                                        fullWidth
                                        variant="outlined"
                                        disabled={attribute.is_confluence_validation_schema_value}
                                        sx={{
                                          '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                              borderColor: errorLocations?.attribute?.[
                                                attribute.attribute_id
                                              ]?.name
                                                ? 'red' // show red border when whitespace error detected
                                                : undefined, // keep MUI default when no error
                                            },
                                            '&:hover fieldset': {
                                              borderColor: errorLocations?.attribute?.[
                                                attribute.attribute_id
                                              ]?.name
                                                ? 'red' // keep red even on hover if invalid
                                                : 'text.secondary', // normal hover color
                                            },
                                            '&.Mui-focused fieldset': {
                                              borderColor: errorLocations?.attribute?.[
                                                attribute.attribute_id
                                              ]?.name
                                                ? 'red' // stay red if invalid
                                                : 'primary.main', //focus purple color
                                            },
                                          },
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <TextField
                                        size="small"
                                        value={attribute.expected_value}
                                        onChange={(e) =>
                                          handleAttributeChange(
                                            scenario.scenario_id,
                                            attribute.attribute_id,
                                            'expected_value',
                                            e.target.value,
                                          )
                                        }
                                        placeholder="Enter expected value"
                                        fullWidth
                                        variant="outlined"
                                        disabled={
                                          attribute.is_confluence_validation_schema_value &&
                                          attribute.attribute_type === 'Static'
                                        }
                                        sx={{
                                          '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                              borderColor: errorLocations?.attribute?.[
                                                attribute.attribute_id
                                              ]?.expected
                                                ? 'red' // red border when whitespace detected at beginning
                                                : undefined, // default otherwise
                                            },
                                            '&:hover fieldset': {
                                              borderColor: errorLocations?.attribute?.[
                                                attribute.attribute_id
                                              ]?.expected
                                                ? 'red'
                                                : 'text.secondary', // normal hover
                                            },
                                            '&.Mui-focused fieldset': {
                                              borderColor: errorLocations?.attribute?.[
                                                attribute.attribute_id
                                              ]?.expected
                                                ? 'red'
                                                : 'primary.main', // purple focus color
                                            },
                                          },
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <FormControl size="small" fullWidth>
                                        <Select
                                          value={attribute.attribute_type}
                                          onChange={(e) =>
                                            handleAttributeChange(
                                              scenario.scenario_id,
                                              attribute.attribute_id,
                                              'attribute_type',
                                              e.target.value,
                                            )
                                          }
                                          disabled={attribute.is_confluence_validation_schema_value}
                                          sx={{
                                            '& .MuiOutlinedInput-root': {
                                              '&:hover fieldset': {
                                                borderColor: 'text.secondary',
                                              },
                                              '&.Mui-focused fieldset': {
                                                borderColor: 'primary.main',
                                              },
                                            },
                                          }}
                                        >
                                          <MenuItem value="Static">Static</MenuItem>
                                          <MenuItem value="Dynamic">Dynamic</MenuItem>
                                        </Select>
                                      </FormControl>
                                    </TableCell>
                                    <TableCell>
                                      {attribute.criticality && (
                                        <Box
                                          sx={eventCardStyles.criticalityBadge(
                                            attribute.criticality,
                                          )}
                                        >
                                          {attribute.criticality}
                                        </Box>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {!schemaLocked && (
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            handleDeleteAttribute(
                                              scenario.scenario_id,
                                              attribute.attribute_id,
                                            )
                                          }
                                          color="error"
                                        >
                                          <DeleteIcon />
                                        </IconButton>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}

                        {!schemaLocked && (
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => handleAddAttribute(scenario.scenario_id)}
                            sx={{
                              mt: 1,
                              color: 'primary.main',
                              '&:hover': {
                                backgroundColor: 'action.hover',
                              },
                            }}
                          >
                            Add Attribute
                          </Button>
                        )}
                      </Box>
                    </Collapse>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}
      </Paper>

      {/* Save/Cancel Actions */}
      <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          onClick={handleBackToFlow}
          sx={{
            borderColor: 'text.secondary',
            color: 'text.secondary',
            '&:hover': {
              borderColor: 'text.secondary',
              backgroundColor: 'action.hover',
            },
          }}
        >
          Back to Flow
        </Button>
        <Button
          variant="contained"
          onClick={handleSaveEvent}
          disabled={
            !hasUnsavedChanges || !currentEvent.event_name.trim() || Boolean(validationError)
          }
          sx={{
            bgcolor: 'primary.main',
            '&:hover': {
              bgcolor: 'primary.light',
            },
            '&:disabled': {
              bgcolor: 'action.disabledBackground',
            },
          }}
        >
          {hasUnsavedChanges ? 'Save Event' : 'Saved'}
        </Button>
      </Box>

      {/* Add Scenario Type Modal */}
      <Dialog
        open={addScenarioModalOpen}
        onClose={() => setAddScenarioModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Scenario</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Scenario Name"
              value={newScenarioType.scenario_name}
              onChange={(e) =>
                setNewScenarioType((prev) => ({
                  ...prev,
                  scenario_name: e.target.value,
                }))
              }
              placeholder="e.g. Button Click Event"
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'text.secondary',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: 'primary.main',
                },
              }}
            />
            <FormControl
              fullWidth
              sx={{
                '& .MuiInputLabel-root.Mui-focused': {
                  color: 'primary.main',
                },
              }}
            >
              <InputLabel>Scenario Type</InputLabel>
              <Select
                value={newScenarioType.scenario_type}
                label="Scenario Type"
                onChange={(e) =>
                  setNewScenarioType((prev) => ({
                    ...prev,
                    scenario_type: e.target.value,
                  }))
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'text.secondary',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              >
                {SCENARIO_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value} disabled={false}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        width: '100%',
                      }}
                    >
                      <span>{type.displayName}</span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {newScenarioType.scenario_type?.startsWith('outbound') && (
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Clone from Inbound Scenario</InputLabel>
                <Select
                  value={newScenarioType.cloneFromInboundId || ''}
                  label="Clone from Inbound Scenario"
                  onChange={(e) =>
                    setNewScenarioType((prev) => ({
                      ...prev,
                      cloneFromInboundId: e.target.value,
                    }))
                  }
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: 'text.secondary' },
                      '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {(currentEvent.scenarios || [])
                    .filter((s) => s.scenario_type === 'inbound')
                    .map((s) => (
                      <MenuItem key={s.scenario_id} value={s.scenario_id}>
                        {s.scenario_name}
                        {s.scenario_db_id ? ` (ID:${s.scenario_db_id})` : ''}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}
            {/* Removed warning messages - multiple inbound scenarios are now allowed */}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAddScenarioModalOpen(false)}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddScenarioType}
            disabled={false}
            sx={{
              bgcolor: 'primary.main',
              '&:hover': {
                bgcolor: 'primary.light',
              },
              '&:disabled': {
                bgcolor: 'action.disabledBackground',
              },
            }}
          >
            Create Scenario Type
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unsaved Changes Dialog */}
      <Dialog open={showUnsavedDialog} onClose={handleCancelNavigation}>
        <DialogTitle>{currentEvent.isNew ? 'Discard New Event?' : 'Unsaved Changes'}</DialogTitle>
        <DialogContent>
          <Typography>
            {currentEvent.isNew
              ? "This new event hasn't been saved yet and will be discarded. Are you sure you want to leave?"
              : 'You have unsaved changes that will be lost. Are you sure you want to leave without saving?'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelNavigation}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            Stay
          </Button>
          <Button
            onClick={() => handleSaveEvent(true)}
            variant="contained"
            sx={{
              bgcolor: 'primary.main',
              '&:hover': {
                bgcolor: 'primary.light',
              },
            }}
          >
            Save & Continue
          </Button>
          <Button onClick={handleConfirmNavigation} color="error">
            {currentEvent.isNew ? 'Discard Event' : 'Discard Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save Progress Dialog */}
      <SaveProgressDialog open={showProgressDialog} progress={saveProgress} />

      <Box
        sx={{
          p: 3,
          opacity: isLoading ? 0 : 1,
          transition: 'opacity 0.2s ease-in-out',
        }}
      ></Box>
    </Box>
  );
}
